import { Box, InputAdornment, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import * as Yup from "yup";
import Input from "../../components/Input";
import Text from "../../components/Text";
import PasswordInput from "../../components/app/PasswordInput";
import StyledButtonV2 from "../../components/app/StyledButtonV2";
import { MailOutlineIcon } from "../../components/svg-icons/material-icons";
import { PATIENT_PATHS, PROVIDER_PATHS, appPaths } from "../../constants/paths";
import { useFcmTokenRegistration } from "../../hooks/fcm/use-fcm-token-registration";
import { setCurrentUser } from "../../hooks/local-storage/use-current-user";
import usePermissionStore from "../../hooks/usePermissionStore";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useProviderProfile } from "../../queries/provider/setting/use-provider-profile";
import { useLogin } from "../../queries/use-Login";
import { useUserProfile } from "../../queries/use-user-profile";
import { identifyUser } from "../../services/mix-panel/mix-panel-util";
import { LOGIN_TOKENS } from "../../tokens/design-tokens";
import {
  setAuthTokens,
  setViewAs as setStoredViewAs,
  setStoredRealm,
  setStoredRole,
  setBaseRole,
  setProviderGroupName,
  setAuthType,
} from "@/utils/auth-storage";
import { getTenantFromHostname } from "../../utils/get-tenant-from-hostname";
import { getErrorSnackbar } from "../../utils/utils";

const LoginV2 = () => {
  const env = import.meta.env.VITE_ENV_NAME;
  const hostNameArr = location.hostname?.split(".");
  const isAdminUrl = hostNameArr[1] === "admin" || hostNameArr[0] === "admin";

  const portal =
    (env === "prod" || isAdminUrl) && (hostNameArr?.[0] === "app" || isAdminUrl)
      ? "admin"
      : hostNameArr[2];

  // const tenantRaw = hostNameArr?.[0] || "";
  // const getTentNameFromURL = tenantRaw.includes("_")
  //   ? tenantRaw.split("_").slice(1).join("_")
  //   : tenantRaw;
  // const tentNameFromURL =
  //   getTentNameFromURL.charAt(0).toUpperCase() + getTentNameFromURL.slice(1);

  const ValidationSchema = Yup.object({
    XTENANTID: Yup.string()
      .when([], {
        is: () => portal === "admin",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(),
      })
      .label("Tenant"),
    username: Yup.string().required("Email is required.").label("Username"),
    password: Yup.string().required("Password is required.").label("Password"),
  });

  const [viewAs, setViewAs] = useState<string>("");
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [token, setToken] = useState<string>("");
  const [role, setRole] = useState("");
  const [requires2fa, setRequires2fa] = useState(false);

  useEffect(() => {}, [portal]);

  const { mutateAsync: LoginReq } = useLogin();
  const { registerFcmToken } = useFcmTokenRegistration();
  const [_, setPermissions] = usePermissionStore();
  const groupName: string = useOutletContext();

  const handleLogin = (val: any) => {
    const payload = {
      username: val.username?.trim(),
      password: val.password?.trim(),
      xTENANTID: getTenantFromHostname(),
      /** the admin portal authenticates against the backend's "staff" portal */
      portal: portal === "admin" ? "staff" : portal,
    };
    LoginReq(payload)
      .then((res: any) => {
        setStoredRealm(val.XTENANTID);

        /** set PERMISSIONS for current logged in USER */
        setPermissions(res?.data?.data?.permissions);

        /** set user data to local storage */
        handleSetUserData(res.data?.data);

        /** request fcm token registration - skip for admin portal */
        if (portal !== "admin") {
          registerFcmToken();
        }
      })
      .catch((err) => {
        // ErrorBoundaryLogHelper(new Error(JSON.stringify({ err, payload })));
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const handleSetUserData = (curUserData: any) => {
    setRole(curUserData.role);
    setStoredRole(curUserData.role);

    const view =
      curUserData.viewAs === "staff" ? "provider" : curUserData.viewAs;
    setViewAs(curUserData.viewAs);
    setStoredViewAs(view);
    setBaseRole(curUserData.baseRole);

    /** set provider group name */
    setProviderGroupName(curUserData?.providerGroupName);

    setAuthTokens(
      curUserData.access_token ?? "",
      curUserData.refresh_token ?? ""
    );
    setToken(curUserData.access_token ?? "");
    if (curUserData.requires_2fa) {
      setRequires2fa(true);
      setAuthType(curUserData.auth_type);
    }
  };

  const {
    values,
    setFieldValue,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: { username: "", password: "", XTENANTID: "" },
    validationSchema: ValidationSchema,
    onSubmit: handleLogin,
  });

  const isProviderPortalToLogin = viewAs === "provider";
  const isProviderStaffPortalToLogin = viewAs === "staff";

  const { data: ProviderProfileData, status } = useProviderProfile(
    getTenantFromHostname(),
    !!token && token !== "" && token !== "none" && isProviderPortalToLogin
  );
  const { data: StaffProfileData, status: staffStatus } = useUserProfile({
    providerGroupId: getTenantFromHostname(),
    disabled: !(
      !!token &&
      token !== "" &&
      token !== "none" &&
      isProviderStaffPortalToLogin
    ),
  });

  useEffect(() => {
    setFieldValue("XTENANTID", getTenantFromHostname());
  }, [setFieldValue]);

  const setUser = (T: any) => {
    // initializeMixpanel();

    setCurrentUser(T);
    if (T) {
      identifyUser({
        id: T.id ?? T.uuid ?? T.userId,
        email: T.email,
        name: T.name,
        firstName: T.firstName,
        lastName: T.lastName,
        phone: T.phone,
        avatar: T.avatar,
        role: T.role,
        roleType: T.roleType,
        tenant: getTenantFromHostname(),
      });
    }
  };

  const isLocalhost = hostNameArr[1] === "localhost";
  const isDataAvailToLogin =
    (ProviderProfileData || StaffProfileData) &&
    (isProviderPortalToLogin || isProviderStaffPortalToLogin);

  useEffect(() => {
    if (!role) return;

    if (viewAs === "patient" && (portal === "patient" || isLocalhost)) {
      setTimeout(() => {
        navigate(PATIENT_PATHS.DASHBOARD);
      }, 0);
      // TODO: replace admin-Super-Admin  `` login temporary added need to replace once done from B.E
    } else if (
      viewAs === "admin" ||
      (env === "prod" &&
        (hostNameArr?.[0] === "app" || hostNameArr?.[1] === "admin"))
    ) {
      setTimeout(() => {
        navigate(appPaths.providerGroupsPath);
      }, 0);
    } else if (
      isDataAvailToLogin &&
      (portal === "provider" ||
        isLocalhost ||
        env === "prod" ||
        env === "sandbox")
    ) {
      if (status === "success") setUser((ProviderProfileData as any)?.data);
      if (staffStatus === "success") setUser((StaffProfileData as any)?.data);

      setTimeout(() => {
        navigate(
          requires2fa ? appPaths.twoFactorAuth : PROVIDER_PATHS.DASHBOARD
        );
      }, 0);
    }
  }, [role, ProviderProfileData, StaffProfileData]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };
  return (
    <Box sx={{ width: "100%" }}>
      {/* Heading & Subheading */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          mb: "32px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: LOGIN_TOKENS.color.primary,
            fontSize: LOGIN_TOKENS.fontSize.heading,
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.medium,
            lineHeight: 1.2,
          }}
        >
          {`Welcome to ${isAdminUrl ? "" : groupName} ${portal?.charAt(0).toUpperCase() + portal?.slice(1)} Portal`}
        </Typography>
        <Typography
          sx={{
            color: LOGIN_TOKENS.color.subheadingText,
            fontSize: LOGIN_TOKENS.fontSize.body,
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.regular,
            lineHeight: 1.6,
          }}
        >
          Enter Your Sign In credentials to access your account.
        </Typography>
      </Box>

      {/* Form Fields */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Input
          label="Email"
          textFieldProps={{
            name: "username",
            value: values.username,
            onKeyDown: handleKeyDown,
            onChange: handleChange,
            onBlur: handleBlur,
            placeholder: groupName
              ? `yourname@${groupName.split(" ").join("").toLowerCase()}.com`
              : portal === "admin"
                ? "yourname@admin.com"
                : "",
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <MailOutlineIcon
                    sx={{
                      color: LOGIN_TOKENS.color.iconGrey,
                      fontSize: LOGIN_TOKENS.fontSize.iconSm,
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
          error={touched?.username && errors?.username}
        />
        <PasswordInput
          label="Password"
          placeholder="Enter Your Password"
          onKeyDown={handleKeyDown}
          error={touched.password && (errors.password as any)}
          value={values.password}
          onChange={(v) => setFieldValue("password", v)}
        />
      </Box>

      {/* Remember Me & Forgot Password */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "20px",
        }}
      >
        {/* note: hiding this as discussed with team in meeting [AUG:13]
         <FormControlLabel
          label={
            <Text
              color={LOGIN_TOKENS.color.rememberMeText}
              fontSize={LOGIN_TOKENS.fontSize.body}
              fontWeight={`${LOGIN_TOKENS.fontWeight.regular}`}
            >
              Remember me
            </Text>
          }
          control={
            <Checkbox
              sx={{
                color: LOGIN_TOKENS.color.checkboxUnchecked,
                "&.Mui-checked": { color: LOGIN_TOKENS.color.primary },
                "& .MuiSvgIcon-root": {
                  fontSize: LOGIN_TOKENS.fontSize.checkboxIcon,
                },
              }}
            />
          }
        /> */}
        {portal !== "admin" && (
          <Text
            sx={{
              cursor: "pointer",
              fontFamily: LOGIN_TOKENS.fontFamily,
              fontWeight: LOGIN_TOKENS.fontWeight.medium,
              fontSize: LOGIN_TOKENS.fontSize.body,
              lineHeight: "125%",
              color: LOGIN_TOKENS.color.primary,
            }}
            onClick={() => navigate("/auth/forgot-password")}
          >
            Forgot Password ?
          </Text>
        )}
      </Box>

      {/* 2FA Note */}
      {/* <Box sx={{ width: "100%", mt: "40px" }}>
        <Typography
          sx={{
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.regular,
            fontSize: LOGIN_TOKENS.fontSize.body,
            lineHeight: 1.6,
            color: LOGIN_TOKENS.color.noteText,
            textAlign: "center",
          }}
        >
          Note: This account uses two-step verification.
          <br />
          You'll need a verification code after your password.
        </Typography>
      </Box> */}

      {/* Login Button */}
      <Box sx={{ width: "100%", mt: "16px" }}>
        <StyledButtonV2
          label="Sign In"
          variant="contained"
          fullWidth
          onClick={() => handleSubmit()}
          sx={{
            py: "16px",
            px: "18px",
            fontSize: LOGIN_TOKENS.fontSize.button,
            fontFamily: LOGIN_TOKENS.fontFamily,
            fontWeight: LOGIN_TOKENS.fontWeight.medium,
            textTransform: "none",
            borderRadius: "4px",
            backgroundColor: LOGIN_TOKENS.color.primary,
            border: `1px solid ${LOGIN_TOKENS.color.primary}`,
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            lineHeight: 1.25,
            "&:hover": { backgroundColor: LOGIN_TOKENS.color.primaryHover },
            height: LOGIN_TOKENS.height,
          }}
        />

        {/* <ArrowForward sx={{ ml: "8px", fontSize: 20 }} /> */}
      </Box>
    </Box>
  );
};

export default LoginV2;
